import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { File, Upload, Download, Trash2 } from 'lucide-react';

const EvidenceVault: React.FC<{ caseId: string }> = ({ caseId }) => {
  const [evidence, setEvidence] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fetchEvidence = async () => {
    try {
      const response = await api.get(`/evidence/case/${caseId}`);
      setEvidence(response.data);
    } catch (error) {
      console.error('Error fetching evidence:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvidence();
  }, [caseId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('caseId', caseId);
    formData.append('title', title);

    try {
      await api.post('/evidence', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setTitle('');
      setSelectedFile(null);
      // Reset input manually because it's uncontrolled for files
      const fileInput = document.getElementById('evidence-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      fetchEvidence();
    } catch (error: any) {
      console.error('Error uploading evidence:', error);
      alert(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div>Loading vault...</div>;

  return (
    <div style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
      <h4 style={{ fontSize: '1.1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <File size={20} /> Evidence Vault
      </h4>

      {/* Upload Form */}
      <form onSubmit={handleUpload} style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <input 
            type="text" 
            placeholder="Document Title (e.g. Sale Deed)" 
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}
          />
          <input 
            id="evidence-file-input"
            type="file" 
            onChange={handleFileChange}
            required
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', backgroundColor: 'white' }}
          />
        </div>
        <button 
          type="submit" 
          disabled={uploading}
          style={{ padding: '0.75rem 1rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold' }}
        >
          <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Evidence Document'}
        </button>
      </form>

      {/* Evidence List */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {evidence.map((item) => (
          <div key={item.id} style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ backgroundColor: '#edf2f7', padding: '0.5rem', borderRadius: '4px' }}>
                <File size={20} color="#1a365d" />
              </div>
              <div>
                <p style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{item.title}</p>
                <p style={{ fontSize: '0.75rem', color: '#718096' }}>Type: {item.fileType} • Added on {new Date(item.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <a href={item.fileUrl} target="_blank" rel="noreferrer" style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #e2e8f0', color: '#4a5568' }}>
                <Download size={16} />
              </a>
              <button style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #e2e8f0', color: '#e53e3e', background: 'none', cursor: 'pointer' }}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {evidence.length === 0 && (
          <p style={{ textAlign: 'center', color: '#718096', fontSize: '0.875rem', padding: '1rem' }}>No evidence uploaded yet.</p>
        )}
      </div>
    </div>
  );
};

export default EvidenceVault;
